import React,{useEffect,useRef,useState} from 'react';
import QRCode from 'qrcode';
import './KioskPage.css';

const API='/api/kiosk';
// VP-003Z — the raw one-time device secret is exchanged, once, for a
// short-lived session token (sent on every kiosk request, kept in memory
// only — never written to storage) and a longer, sliding-window refresh
// token (persisted, used only to mint a fresh session token). STORAGE_KEY
// now holds the refresh token, not the raw secret. LEGACY_SECRET_KEY is the
// pre-VP-003Z storage key: a kiosk that still has one (and no refresh token
// yet) is transparently migrated on its next load — the raw secret is
// exchanged via /session/bootstrap and then discarded, never persisted.
const STORAGE_KEY='kioskRefreshToken';
const LEGACY_SECRET_KEY='kioskDeviceSecret';
const HEARTBEAT_MS=90000;
// Shorter than the 60s action token TTL on purpose: the employee should
// never be staring at another person's name on an unattended kiosk for
// anywhere close to the token's full lifetime. Not a token-lifetime change.
const VERIFIED_IDLE_MS=20000;
// Only a genuine device-authorization failure ever wipes the persisted
// credential. KIOSK_LOCATION_UNAVAILABLE (location temporarily deactivated),
// wrong PIN, an expired action token, or a network/server error are all
// retryable conditions that must NOT be treated as "this device was
// revoked" — the QR-rotation retry loop already self-heals once the
// underlying condition clears, with no reprovisioning required.
// KIOSK_SESSION_EXPIRED is likewise never in this set: it means only the
// short-lived session token needs renewal (handled transparently by call()
// below), not that the device itself was revoked. Neither is
// KIOSK_SESSION_REFRESH_RACE (VP-003Z correction) — it means a concurrent
// renewal (another tab, or another timer callback) already rotated this
// device's refresh token first; retryable, never a device failure.
const DEVICE_FAILURE_CODES=new Set(['KIOSK_UNAUTHORIZED']);
// Bounded per-endpoint client timeouts so a hung request can never leave
// inFlight/busy stuck or an employee's name on screen indefinitely. Values
// are sized to each endpoint's own work, not copied from the authenticated
// app's shared apiClient: status/verify/QR are each a single light query (or
// one bcrypt compare); action additionally consumes the token and writes an
// audit event, so it gets a longer bound. None of this changes the 20s
// privacy timeout or the server's 60s action-token TTL.
const STATUS_TIMEOUT_MS=8000;
const VERIFY_TIMEOUT_MS=8000;
const QR_TIMEOUT_MS=8000;
const ACTION_TIMEOUT_MS=12000;
const CLIENT_TIMEOUT_MESSAGE='The request timed out. Please try again.';

async function rawCall(path,headerName,headerValue,{method='POST',body,timeoutMs}={}){
 const controller=new AbortController();
 const timer=timeoutMs?setTimeout(()=>controller.abort(),timeoutMs):null;
 let response;
 try{response=await fetch(`${API}${path}`,{method,headers:{'Content-Type':'application/json',[headerName]:headerValue},body:method==='GET'?undefined:JSON.stringify(body||{}),signal:controller.signal});}
 catch(error){
  if(timer)clearTimeout(timer);
  // A client-side abort proves nothing about whether the server received or
  // finished the request — never let this look like the same thing as a
  // server-returned failure. Callers that need ambiguity-safe copy (act())
  // check error.code === 'CLIENT_TIMEOUT' explicitly.
  if(error?.name==='AbortError'){const timeoutError=new Error(CLIENT_TIMEOUT_MESSAGE);timeoutError.code='CLIENT_TIMEOUT';throw timeoutError;}
  throw error;
 }
 if(timer)clearTimeout(timer);
 const json=await response.json().catch(()=>({}));
 if(!response.ok){const error=new Error(json.error||'The kiosk could not complete this request.');error.code=json.code;throw error;}
 return json.data;
}
const bootstrapSession=(deviceSecret,timeoutMs)=>rawCall('/session/bootstrap','X-Kiosk-Device-Secret',deviceSecret,{timeoutMs});
const renewSession=(refreshToken,timeoutMs)=>rawCall('/session/renew','X-Kiosk-Refresh-Token',refreshToken,{timeoutMs});

export default function KioskPage(){
 const [deviceState,setDeviceState]=useState('checking'); // checking | setup | revoked | ready
 const [setupInput,setSetupInput]=useState('');
 const [setupError,setSetupError]=useState('');
 const [employeeNumber,setEmployeeNumber]=useState('');const [pin,setPin]=useState('');const [verified,setVerified]=useState(null);const [message,setMessage]=useState('');const [busy,setBusy]=useState(false);const timer=useRef();const [qrImage,setQrImage]=useState('');const [qrUnavailable,setQrUnavailable]=useState(false);
 const [enrolling,setEnrolling]=useState(false),[activationCode,setActivationCode]=useState(''),[pinConfirmation,setPinConfirmation]=useState('');
 const [deviceInfo,setDeviceInfo]=useState(null);const [now,setNow]=useState(()=>new Date());const idleTimer=useRef();
 // A synchronous ref, not the `busy` state, guards against a second fast
 // tap: React state updates are batched/async, so two taps in the same
 // tick can both still observe `busy === false`. A ref mutates immediately.
 const inFlight=useRef(false);
 // The session token never touches storage — memory only. The refresh
 // token mirrors what is persisted under STORAGE_KEY; keeping a copy here
 // avoids re-reading storage on every call and lets renewal update both in
 // one place.
 const session=useRef({token:'',refreshToken:''});
 // VP-003Z correction — a single shared in-flight renewal promise. Startup,
 // the heartbeat, QR rotation, verify, and action can all independently
 // notice an expired session around the same moment (most obviously right
 // at the 15-minute session-token boundary). Without this, each would fire
 // its own /session/renew using the same refresh token, racing the server's
 // atomic rotation for no reason — only one can ever win, and the others
 // would previously have been misread as device failures. Concurrent
 // callers within this tab now join the SAME renewal attempt instead.
 const renewInFlight=useRef(null);
 // A single renewal attempt, with the multi-tab/rotation-race recovery the
 // server's atomic compare-and-swap makes possible: a renewal can fail with
 // KIOSK_SESSION_REFRESH_RACE (this device's refresh token was valid when
 // looked up but a concurrent renewal elsewhere — another tab, a restored
 // tab, a duplicate process — rotated it first) or with KIOSK_UNAUTHORIZED
 // (this token no longer matches anything, which a *slightly* late-arriving
 // sibling renewal produces just as often as a genuinely invalid token).
 // Neither should be trusted at face value: always re-read the CURRENT
 // refresh token from shared storage first. If it has changed since this
 // attempt started, some other actor's renewal already won and persisted
 // it — retry with that token instead of failing. Only once storage still
 // matches what was just tried does a failure get treated as genuine
 // (surfaced as-is to the caller; KIOSK_UNAUTHORIZED from there clears the
 // device via handleFailure, exactly as before). This never writes storage
 // on failure — only a successful renewSession() call ever does.
 const renewWithRecovery=async(timeoutMs,attempted=new Set())=>{
  const refreshToken=localStorage.getItem(STORAGE_KEY)||session.current.refreshToken;
  attempted.add(refreshToken);
  try{
   const refreshed=await renewSession(refreshToken,timeoutMs);
   session.current={token:refreshed.sessionToken,refreshToken:refreshed.refreshToken};
   localStorage.setItem(STORAGE_KEY,refreshed.refreshToken);
   return refreshed;
  }catch(error){
   const current=localStorage.getItem(STORAGE_KEY);
   if(current&&current!==refreshToken&&!attempted.has(current))return renewWithRecovery(timeoutMs,attempted);
   throw error;
  }
 };
 const renewShared=timeoutMs=>{
  if(renewInFlight.current)return renewInFlight.current;
  const attempt=renewWithRecovery(timeoutMs).finally(()=>{renewInFlight.current=null;});
  renewInFlight.current=attempt;
  return attempt;
 };

 // Every protected kiosk endpoint call goes through here. A
 // KIOSK_SESSION_EXPIRED failure (unknown, expired, or the owning device was
 // revoked — indistinguishable, by design, from the endpoint's point of
 // view) is handled transparently: renew once (via the shared, mutexed,
 // race-recovering renewShared above) and retry the original call. A renew
 // failure propagates as-is (ordinarily KIOSK_UNAUTHORIZED, or the
 // retryable KIOSK_SESSION_REFRESH_RACE) to the caller's existing
 // handleFailure logic, unchanged.
 const call=async(path,{method='POST',body,timeoutMs}={})=>{
  try{return await rawCall(path,'X-Kiosk-Session-Token',session.current.token,{method,body,timeoutMs});}
  catch(firstError){
   if(firstError?.code!=='KIOSK_SESSION_EXPIRED')throw firstError;
   await renewShared(timeoutMs);
   return rawCall(path,'X-Kiosk-Session-Token',session.current.token,{method,body,timeoutMs});
  }
 };
 const checkStatus=timeoutMs=>call('/status',{method:'GET',timeoutMs});

 // Only the device credential is ever persisted. Employee identity, PIN,
 // and action tokens live in component state only and never touch storage.
 const clearDevice=notice=>{
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(LEGACY_SECRET_KEY);
  session.current={token:'',refreshToken:''};
  setEmployeeNumber('');setPin('');setActivationCode('');setPinConfirmation('');setEnrolling(false);setVerified(null);setQrImage('');setQrUnavailable(false);setDeviceInfo(null);clearTimeout(timer.current);clearTimeout(idleTimer.current);setSetupInput('');setSetupError(notice||'');setDeviceState('revoked');
 };
 const handleFailure=error=>{if(error?.code&&DEVICE_FAILURE_CODES.has(error.code)){clearDevice('This kiosk is no longer authorized. Contact your administrator to reprovision it.');return true;}return false;};

 // Establishes (or re-establishes) a session from whatever credential is on
 // hand — a persisted refresh token, or (migration path) a legacy raw
 // device secret — then validates it via /status before ever showing the
 // kiosk as ready, exactly as VP-003V required for the raw secret.
 // Non-legacy renewal goes through renewShared, so a startup renewal racing
 // another tab's startup renewal of the same stored refresh token gets the
 // same multi-tab recovery as every other renewal (and already persists the
 // result itself on success). The legacy/bootstrap path persists the
 // resulting refresh token and clears the legacy secret only after /status
 // validates the new session; touches nothing in storage on failure.
 const establishSession=async(credential,{legacy,timeoutMs})=>{
  if(!legacy){await renewShared(timeoutMs);return checkStatus(timeoutMs);}
  const tokens=await bootstrapSession(credential,timeoutMs);
  session.current={token:tokens.sessionToken,refreshToken:tokens.refreshToken};
  const result=await checkStatus(timeoutMs);
  localStorage.setItem(STORAGE_KEY,tokens.refreshToken);
  localStorage.removeItem(LEGACY_SECRET_KEY);
  return result;
 };

 // Startup: never show a ready kiosk on stored data alone — validate first.
 useEffect(()=>{
  const storedRefreshToken=localStorage.getItem(STORAGE_KEY);
  const legacySecret=localStorage.getItem(LEGACY_SECRET_KEY);
  if(!storedRefreshToken&&!legacySecret){setDeviceState('setup');return undefined;}
  let cancelled=false;
  const check=async()=>{
   try{const result=await establishSession(storedRefreshToken||legacySecret,{legacy:!storedRefreshToken,timeoutMs:STATUS_TIMEOUT_MS});if(!cancelled){setDeviceInfo(result.device||null);setDeviceState('ready');}}
   catch(error){
    if(cancelled)return;
    if(error?.code==='KIOSK_UNAUTHORIZED'){clearDevice('This kiosk is no longer authorized. Contact your administrator to reprovision it.');return;}
    setTimeout(check,15000); // transient/network failure only — keep the credential and retry
   }
  };
  check();
  return()=>{cancelled=true;};
 // establishSession (and the renewShared/call/checkStatus chain it calls)
 // closes over nothing reactive — only refs (session, renewInFlight, stable
 // across renders by React's own guarantee) and module-scope constants and
 // functions (STORAGE_KEY, bootstrapSession, renewSession, rawCall). Its
 // behavior is identical on every render regardless of which render's
 // closure instance runs, so listing it as a dependency would only force
 // this effect to re-run on unrelated re-renders without changing what it
 // does. Wrapping it (and the chain beneath it, which every other kiosk
 // call also shares) in useCallback purely to silence this would touch the
 // just-reviewed CAS/renewal-mutex code across every call site for no
 // behavioral benefit — the same justified-omission pattern already used
 // below for checkStatus/call/handleFailure in the heartbeat and QR effects.
 // eslint-disable-next-line react-hooks/exhaustive-deps
 },[]);

 // Heartbeat while ready: detects revocation on a bounded cadence, not just on the next punch attempt.
 useEffect(()=>{
  if(deviceState!=='ready')return undefined;
  let stopped=false;let heartbeatTimer;
  const beat=async()=>{try{const result=await checkStatus(STATUS_TIMEOUT_MS);if(!stopped)setDeviceInfo(result.device||null);}catch(error){if(!stopped&&handleFailure(error))return;}if(!stopped)heartbeatTimer=setTimeout(beat,HEARTBEAT_MS);};
  heartbeatTimer=setTimeout(beat,HEARTBEAT_MS);
  return()=>{stopped=true;clearTimeout(heartbeatTimer);};
 // eslint-disable-next-line react-hooks/exhaustive-deps
 },[deviceState]);

 useEffect(()=>{
  if(deviceState!=='ready')return undefined;
  let stopped=false;let rotateTimer;
  const rotate=async()=>{try{const challenge=await call('/qr/challenge',{timeoutMs:QR_TIMEOUT_MS});const image=await QRCode.toDataURL(challenge.mobileUrl,{width:220,margin:1,errorCorrectionLevel:'M'});if(!stopped){setQrImage(image);setQrUnavailable(false);rotateTimer=setTimeout(rotate,50000);}}catch(error){if(stopped)return;if(handleFailure(error))return;setQrImage('');setQrUnavailable(true);rotateTimer=setTimeout(rotate,15000);}};
  rotate();
  return()=>{stopped=true;clearTimeout(rotateTimer);clearTimeout(timer.current);};
 // eslint-disable-next-line react-hooks/exhaustive-deps
 },[deviceState]);

 // Live clock — purely cosmetic orientation for the appliance. Punch
 // timestamps themselves are always assigned server-side.
 useEffect(()=>{const clock=setInterval(()=>setNow(new Date()),1000);return()=>clearInterval(clock);},[]);

 // Idle privacy timeout: once verification succeeds, an employee's name and
 // live action token must not sit on screen indefinitely if they walk away
 // before choosing an action. Interaction-aware: any pointer/keyboard
 // activity on the verified screen (bumpIdle, wired to the wrapper below)
 // pushes the deadline back out, so an engaged employee is never cut off
 // mid-decision — only genuine inactivity clears the screen.
 const fireIdleTimeout=()=>{
  // A punch request is in flight: don't yank the action token out from under
  // it. act()'s own success/failure handling calls reset() the moment the
  // request settles — that's what actually clears the screen in this case.
  if(inFlight.current)return;
  reset('Timed out for privacy. Please verify again.');
 };
 const bumpIdle=()=>{clearTimeout(idleTimer.current);idleTimer.current=setTimeout(fireIdleTimeout,VERIFIED_IDLE_MS);};
 useEffect(()=>{
  if(!verified)return undefined;
  bumpIdle();
  return()=>clearTimeout(idleTimer.current);
 // eslint-disable-next-line react-hooks/exhaustive-deps
 },[verified]);

 const activate=async e=>{
  e.preventDefault();
  if(inFlight.current)return;
  inFlight.current=true;
  const value=setupInput.trim();
  setBusy(true);setSetupError('');
  try{const result=await establishSession(value,{legacy:true,timeoutMs:STATUS_TIMEOUT_MS});setDeviceInfo(result.device||null);setSetupInput('');setDeviceState('ready');setMessage('Kiosk device ready.');}
  catch(error){session.current={token:'',refreshToken:''};setSetupError(error.message||'Kiosk device is not authorized.');}
  finally{setBusy(false);inFlight.current=false;}
 };

 const reset=(success='')=>{setEmployeeNumber('');setPin('');setVerified(null);setActivationCode('');setPinConfirmation('');setEnrolling(false);setMessage(success);clearTimeout(timer.current);if(success)timer.current=setTimeout(()=>setMessage(''),4000);window.history.replaceState(null,'','/kiosk');};
 const verify=async e=>{e.preventDefault();if(inFlight.current)return;inFlight.current=true;setBusy(true);setMessage('');try{setVerified(await call('/verify',{body:{employeeNumber:employeeNumber.trim(),pin},timeoutMs:VERIFY_TIMEOUT_MS}));setPin('');}catch(error){if(handleFailure(error))return;reset();setMessage(error.message);}finally{setBusy(false);inFlight.current=false;}};
 const enroll=async e=>{e.preventDefault();if(inFlight.current)return;if(pin!==pinConfirmation){setMessage('PIN confirmation does not match.');return;}inFlight.current=true;setBusy(true);setMessage('');try{await call('/enroll',{body:{employeeNumber:employeeNumber.trim(),activationCode,pin},timeoutMs:ACTION_TIMEOUT_MS});reset('Kiosk PIN created. You can use it now.');}catch(error){if(handleFailure(error))return;setPin('');setPinConfirmation('');setActivationCode('');setMessage(error.message);}finally{setBusy(false);inFlight.current=false;}};
 const act=async action=>{if(inFlight.current)return;inFlight.current=true;setBusy(true);try{await call('/action',{body:{actionToken:verified.actionToken,action},timeoutMs:ACTION_TIMEOUT_MS});reset('Timekeeping action completed.');}catch(error){
  // A client timeout on /action proves nothing about server completion — the
  // punch may have gone through. Never claim failure here; never auto-retry
  // the (possibly already-consumed) action token. Send the employee back to
  // a neutral, re-verifiable screen where the next /verify call returns
  // server-authoritative state.
  if(error?.code==='CLIENT_TIMEOUT'){reset("We couldn't confirm the timekeeping action. Please verify your status before trying again.");return;}
  if(handleFailure(error))return;reset();setMessage(error.message);
 }finally{setBusy(false);inFlight.current=false;}};

 if(deviceState==='checking')return <main className="kiosk"><section className="kiosk-card" aria-live="polite"><h1>Company Time Clock</h1><p role="status">Checking this kiosk&rsquo;s authorization&hellip;</p></section></main>;

 if(deviceState!=='ready')return <main className="kiosk"><form className="kiosk-card" onSubmit={activate}><h1>{deviceState==='revoked'?'Reprovision this kiosk':'Set up this kiosk'}</h1><p>{deviceState==='revoked'?'This device credential is no longer valid. Enter a new one-time device credential from an authorized manager.':'Enter the one-time device credential provided by an authorized manager.'}</p><label>Device credential<input type="password" autoComplete="off" value={setupInput} onChange={e=>setSetupInput(e.target.value)} required/></label><button disabled={busy} type="submit">{busy?'Checking…':'Activate device'}</button>{setupError&&<p role="status">{setupError}</p>}</form></main>;

 return <main className="kiosk"><section className="kiosk-card" aria-live="polite"><h1>Company Time Clock</h1><p className="kiosk-context">{deviceInfo&&<span>{deviceInfo.locationName}{deviceInfo.locationName&&deviceInfo.displayName?' · ':''}{deviceInfo.displayName}</span>}<time dateTime={now.toISOString()}>{now.toLocaleString(undefined,{weekday:'short',month:'short',day:'numeric',hour:'numeric',minute:'2-digit'})}</time></p>{message&&<p className="kiosk-message" role="status">{message}</p>}{!verified?(enrolling?<form onSubmit={enroll}><h2>Set Up Kiosk PIN</h2><label>Employee number<input autoComplete="off" value={employeeNumber} onChange={e=>setEmployeeNumber(e.target.value)} required autoFocus/></label><label>Activation code<input autoComplete="off" value={activationCode} onChange={e=>setActivationCode(e.target.value)} required/></label><label>Choose PIN<input type="password" inputMode="numeric" autoComplete="new-password" pattern="[0-9]{6,12}" value={pin} onChange={e=>setPin(e.target.value)} required/></label><label>Confirm PIN<input type="password" inputMode="numeric" autoComplete="new-password" pattern="[0-9]{6,12}" value={pinConfirmation} onChange={e=>setPinConfirmation(e.target.value)} required/></label><button disabled={busy} type="submit">{busy?'Creating PIN…':'Create PIN'}</button><button className="kiosk-cancel" type="button" onClick={()=>reset()}>Cancel</button></form>:<><section className="kiosk-qr" aria-label="Scan with your phone"><h2>Scan with your phone</h2>{qrImage?<img src={qrImage} alt="Rotating mobile punch QR code"/>:<p>{qrUnavailable?'Mobile scan is temporarily unavailable.':'Preparing mobile scan…'}</p>}<p>QR codes rotate every minute. You will still verify your identity on your phone.</p></section><div className="kiosk-divider">Use Employee Number + PIN</div><form onSubmit={verify}><label>Employee number<input inputMode="numeric" autoComplete="off" value={employeeNumber} onChange={e=>setEmployeeNumber(e.target.value)} required autoFocus/></label><label>Private PIN<input type="password" inputMode="numeric" autoComplete="off" value={pin} onChange={e=>setPin(e.target.value)} required/></label><button disabled={busy} type="submit">{busy?'Verifying…':'Continue'}</button></form><button className="kiosk-cancel" type="button" onClick={()=>{setEmployeeNumber('');setPin('');setEnrolling(true)}}>Set Up Kiosk PIN</button></>):<div onPointerDown={bumpIdle} onKeyDown={bumpIdle}><p className="kiosk-greeting">Hello, {verified.employee.displayName}.</p><div className="kiosk-actions">{verified.actions.map(action=><button disabled={busy} key={action} onClick={()=>act(action)}>{action.replaceAll('_',' ')}</button>)}</div><button className="kiosk-cancel" onClick={()=>reset()}>Cancel</button></div>}</section></main>;
}
